// @ts-check
// SCIL-18101: URL contract for carrying an org-profile SciSearch AI query over to
// the MAIN search page when the org endpoint says it belongs there.
//
// The org-page modal opens a NEW TAB (window.open) at the main search route with:
//   ?sciAiQuery=<query>
// The main search page reads the param, auto-opens the NLP modal prefilled with
// the query, and strips the param so a refresh doesn't re-trigger.
//
// It is only ever a search string — no personal data. Capped so a hand-edited
// URL cannot blow up the modal.

/** Query text param. */
export const SCIAI_QUERY_PARAM = 'sciAiQuery';

/** Hard cap on the carried query length (matches the stage-one input intent). */
export const SCIAI_QUERY_MAX_CHARS = 500;

/**
 * Build the main-search URL that carries an org query over. Returns a root-relative
 * path (main search lives at `/`) so it works under any host.
 *
 * @param {string} query
 * @returns {string}
 */
export function buildSciAiRedirectUrl(query) {
  const params = new URLSearchParams();
  params.set(SCIAI_QUERY_PARAM, (query ?? '').slice(0, SCIAI_QUERY_MAX_CHARS));
  return `/?${params.toString()}`;
}

/**
 * Open main search in a new tab with the query carried over, so the user keeps
 * their profile. A blocked popup falls back to same-tab navigation so the query
 * is never silently lost.
 *
 * @param {string} query
 */
export function openMainSearchWithQuery(query) {
  const url = buildSciAiRedirectUrl(query);
  const opened = window.open(url, '_blank');
  if (!opened) {
    window.location.assign(url);
  }
}

/**
 * Read + validate the carried query from a search string (e.g. window.location.search).
 * Returns null when no query param is present.
 *
 * @param {string} search
 * @returns {{ query: string } | null}
 */
export function readSciAiRedirectParams(search) {
  const params = new URLSearchParams(search);
  const rawQuery = params.get(SCIAI_QUERY_PARAM);
  if (!rawQuery) {
    return null;
  }
  return { query: rawQuery.slice(0, SCIAI_QUERY_MAX_CHARS) };
}
